import * as c from "@yahshi/contracts";
import type { z } from "zod";
import { M1ApiError } from "../../m1-app/data/M1AppApi";
import type { ConfigurationApi } from "./ConfigurationApi";

type App = { id: string; name: string; code: string; status: "active" | "disabled" };
type Audit = c.ConfigurationAuditPage["items"][number];
const messages: Record<string, string> = {
  CONFIGURATION_UNCHANGED: "草稿与最新发布内容相同，无需重复发布",
  CONFIGURATION_IN_USE: "配置仍有关联应用，请先解除关联",
  CONFIGURATION_IDEMPOTENCY_CONFLICT: "操作标识已用于其他请求，请刷新后重试",
  CONFIGURATION_INVALID_TRANSITION: "当前配置或应用状态不允许此操作",
  CONFIGURATION_APP_NOT_FOUND: "关联应用不存在，请重新选择",
  CONFIGURATION_INVALID_INPUT: "配置信息格式有误，请检查输入",
  CONFIGURATION_VERSION_CONFLICT: "配置或关联已更新，请重新加载后重试",
  CONFIGURATION_NOT_FOUND: "配置或发布版本不存在，请重新加载",
};
const fail = (code: string): never => { throw new M1ApiError(code, messages[code] ?? "配置操作失败，请重试"); };
const parse = <T>(schema: z.ZodType<T>, input: unknown): T => {
  const result = schema.safeParse(input);
  return result.success ? result.data : fail("CONFIGURATION_INVALID_INPUT");
};
const clone = <T>(value: T): T => structuredClone(value);
const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
const now = () => new Date().toISOString();
const actor = "dev-admin";

/** Ephemeral development data only; mutations share one queue like database transactions. */
export function createDevelopmentConfigurationApi(options: { getApp: (id: string) => Promise<App> }): ConfigurationApi {
  const profiles = new Map<string, c.ConfigurationProfile>();
  const history = new Map<string, c.ConfigurationRevision[]>();
  const bindings = new Map<string, c.ConfigurationBinding>();
  const audits: Audit[] = [];
  const receipts = new Map<string, { fingerprint: string; result: c.ConfigurationProfile }>();
  let queue: Promise<unknown> = Promise.resolve();
  const transaction = <T>(action: () => T | Promise<T>): Promise<T> => {
    const result = queue.then(action);
    queue = result.catch(() => undefined);
    return result.then(clone);
  };
  const get = (id: string) => profiles.get(parse(c.configurationIdSchema, id)) ?? fail("CONFIGURATION_NOT_FOUND");
  const app = async (id: string) => {
    const appId = parse(c.configurationAppIdSchema, id);
    try { return await options.getApp(appId); } catch { return fail("CONFIGURATION_APP_NOT_FOUND"); }
  };
  const check = (actual: number, expected: number) => {
    if (actual !== expected) fail("CONFIGURATION_VERSION_CONFLICT");
  };
  const bump = (value: number) => value < 2147483647 ? value + 1 : fail("CONFIGURATION_INVALID_TRANSITION");
  const touch = (p: c.ConfigurationProfile) => { p.version = bump(p.version); p.updatedAt = now(); p.updatedBy = actor; };
  const record = (p: c.ConfigurationProfile, action: Audit["action"], appId: string | null = null, binding?: c.ConfigurationBinding) => {
    audits.unshift({ id: crypto.randomUUID(), actorId: actor, action, profileId: p.id, appId,
      version: binding?.version ?? p.version, revision: binding?.revision ?? p.published?.revision ?? null, requestId: null, createdAt: now() });
  };
  const page = <T>(items: T[], input: unknown) => {
    const q = parse(c.configurationPaginationSchema, input);
    return { items: items.slice((q.page - 1) * q.pageSize, q.page * q.pageSize), total: items.length };
  };
  const idempotent = (key: string, fingerprint: unknown, action: () => c.ConfigurationProfile) => {
    const encoded = JSON.stringify(fingerprint);
    const receipt = receipts.get(key);
    if (receipt) return receipt.fingerprint === encoded ? receipt.result : fail("CONFIGURATION_IDEMPOTENCY_CONFLICT");
    const result = action();
    receipts.set(key, { fingerprint: encoded, result: clone(result) });
    return result;
  };
  const emptyBinding = (appId: string): c.ConfigurationBinding => ({ appId, version: 0, mode: "fixed", profileId: null, profileName: null, revision: null, updatedAt: null, updatedBy: null });
  const currentBinding = (appId: string) => {
    const b = bindings.get(appId) ?? emptyBinding(appId);
    return { ...b, profileName: b.profileId ? get(b.profileId).name : null };
  };
  const publish = (id: string, input: c.ConfigurationPublishInput | c.ConfigurationRollbackInput, rollback: boolean) => {
    const value = rollback ? parse(c.configurationRollbackSchema, input) : parse(c.configurationPublishSchema, input);
    parse(c.configurationIdSchema, id);
    return idempotent(value.mutationId, [rollback ? "rollback" : "publish", id, value], () => {
      const p = get(id); check(p.version, value.expectedVersion);
      if (p.status !== "active") fail("CONFIGURATION_INVALID_TRANSITION");
      const target = rollback ? (value as c.ConfigurationRollbackInput).targetRevision : null;
      if (target === p.published?.revision) fail("CONFIGURATION_UNCHANGED");
      const source = target === null ? p.draft : history.get(id)?.find(r => r.revision === target)?.values;
      if (!source) fail("CONFIGURATION_NOT_FOUND");
      if (!rollback && same(source, p.published?.values)) fail("CONFIGURATION_UNCHANGED");
      const revision = bump(p.published?.revision ?? 0);
      touch(p);
      p.published = { revision, values: clone(source!), publishedAt: now(), publishedBy: actor, action: rollback ? "rollback" : "publish", sourceRevision: target };
      if (rollback) p.draft = clone(source!);
      history.get(id)!.unshift(clone(p.published!)); record(p, rollback ? "rollback" : "publish");
      return p;
    });
  };
  return {
    list: (input = {}) => transaction(() => {
      const q = parse(c.configurationListQuerySchema, input);
      const keyword = q.keyword.toLocaleLowerCase();
      const items = [...profiles.values()].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt) || a.id.localeCompare(b.id)).filter(p => (q.status === "all" || p.status === q.status)
        && (q.publishedOnly !== "true" || p.published !== null)
        && `${p.name} ${p.description}`.toLocaleLowerCase().includes(keyword));
      return page(items, { page: q.page, pageSize: q.pageSize });
    }),
    create: input => transaction(() => {
      const v = parse(c.configurationCreateSchema, input);
      return idempotent(v.mutationId, ["create", v], () => {
        const timestamp = now();
        const p: c.ConfigurationProfile = { id: crypto.randomUUID(), module: "application_public", schemaVersion: 1, environment: "development", name: v.name, description: v.description, status: "active", version: 1, draft: clone(v.values), published: null, boundAppCount: 0, createdAt: timestamp, updatedAt: timestamp, updatedBy: actor };
        profiles.set(p.id, p); history.set(p.id, []); record(p, "create"); return p;
      });
    }),
    get: id => transaction(() => get(id)),
    update: (id, input) => transaction(() => {
      const v = parse(c.configurationUpdateSchema, input); const p = get(id); check(p.version, v.expectedVersion);
      if (p.name === v.name && p.description === v.description && same(p.draft, v.values)) return p;
      touch(p); p.name = v.name; p.description = v.description; p.draft = clone(v.values); record(p, "update"); return p;
    }),
    publish: (id, input) => transaction(() => publish(id, input, false)),
    rollback: (id, input) => transaction(() => publish(id, input, true)),
    setStatus: (id, input) => transaction(() => {
      const v = parse(c.configurationStatusSchema, input); const p = get(id); check(p.version, v.expectedVersion);
      if (p.status === v.status) return p;
      if (v.status === "disabled" && p.boundAppCount) fail("CONFIGURATION_IN_USE");
      touch(p); p.status = v.status; record(p, v.status === "active" ? "enable" : "disable"); return p;
    }),
    revisions: (id, input = {}) => transaction(() => { get(id); return page(history.get(id)!, input); }),
    audit: (id, input = {}) => transaction(() => { get(id); return page(audits.filter(a => a.profileId === id), input); }),
    bindings: (id, input = {}) => transaction(async () => {
      get(id); const selected = page([...bindings.values()].filter(b => b.profileId === id), input);
      const items = await Promise.all(selected.items.map(async b => { const a = await app(b.appId); return { ...currentBinding(b.appId), appName: a.name, appCode: a.code, appStatus: a.status }; }));
      return { items, total: selected.total };
    }),
    getBinding: id => transaction(async () => { const a = await app(id); return currentBinding(a.id); }),
    bind: (id, input) => transaction(async () => {
      const v = parse(c.configurationBindingUpdateSchema, input); const a = await app(id); const previous = currentBinding(a.id);
      check(previous.version, v.expectedVersion);
      const p = v.profileId ? get(v.profileId) : null;
      if (p && (a.status !== "active" || p.status !== "active")) fail("CONFIGURATION_INVALID_TRANSITION");
      if (p && !history.get(p.id)!.some(r => r.revision === v.revision)) fail("CONFIGURATION_NOT_FOUND");
      if (previous.profileId === v.profileId && previous.revision === v.revision) return previous;
      const next: c.ConfigurationBinding = { appId: a.id, version: bump(previous.version), mode: "fixed", profileId: v.profileId, profileName: p?.name ?? null, revision: v.revision, updatedAt: now(), updatedBy: actor };
      bindings.set(a.id, next);
      if (previous.profileId && previous.profileId !== v.profileId) { const old = get(previous.profileId); old.boundAppCount--; record(old, "unbind", a.id, { ...next, revision: previous.revision }); }
      if (p) { if (previous.profileId !== p.id) p.boundAppCount++; record(p, "bind", a.id, next); }
      return next;
    }),
  };
}
